import { createContext, useContext, useMemo, useState } from "react";
import { rounding } from "utils/rounding";
import { Recipe as ApiRecipe, Recipe } from "../../types/recipe";

export type TProduct = {
  id: string;
  name: string;
  price: {
    baseFactor: number;
    discountPercentage: number;
    perUnit: number;
    value: number;
    fullValue: number;
  };
  unitAmount: number;
  baseUnit: string;
  baseUnitShort: string;
  volumeLabel: string;
  volumeLabelShort: string;
  images: string[];
  weight: number;
  weightUnit: string;
  category: string;
  brand?: {
    name: string;
    nameShort: string;
  };
  maxQuantity: number;
};

export type TCartProduct = {
  quantity: number;
  product: TProduct;
  options: TProduct[];
};

export type TCartRecipe = {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  ingredients: TCartProduct[];
  quantity: number;
  apiData?: ApiRecipe; // Optional reference to the full API data
};

type TCartContext = {
  cart: { [id: string]: TCartProduct };
  suggestedRecipes: Recipe[];
  recipeList: TCartRecipe[];
  itemList: TCartProduct[];
  expandedOptions: string | undefined;
  total: number;
  step: "recipe" | "cart";
  serviceId: "gurkerl" | "billa";
  searchQuery: string; // Add search query tracking
  setStep: (step: "recipe" | "cart") => void;
  setSuggestedRecipes: (recipes: Recipe[]) => void;
  setSearchQuery: (query: string) => void; // Add setter for search query
  checkout: () => void;
  setExpandedOptions: (
    value:
      | string
      | undefined
      | ((value: string | undefined) => string | undefined)
  ) => void;
  swapItem: (id: string, product: TProduct) => void;
  addToCart: (products: TCartProduct[]) => void;
  removeFromCart: (ids: string[]) => void;
  increaseQuantity: (id: string, quantity?: number) => void;
  decreaseQuantity: (id: string, quantity?: number) => void;
  addRecipe: (recipe: TCartRecipe) => void;
  removeRecipe: (id: string) => void;
  swapRecipeItem: (recipeId: string, itemId: string, product: TProduct) => void;
  increaseRecipeItemQuantity: (
    recipeId: string,
    itemId: string,
    quantity?: number
  ) => void;
  decreaseRecipeItemQuantity: (
    recipeId: string,
    itemId: string,
    quantity?: number
  ) => void;
  increaseRecipeQuantity: (recipeId: string) => void;
  decreaseRecipeQuantity: (recipeId: string) => void;
  addRecipeItemsToCart: (id: string, items: TCartProduct[]) => void;
  removeRecipeItemsFromCart: (recipeId: string) => void;
  setRecipeList: (
    recipes: TCartRecipe[] | ((prev: TCartRecipe[]) => TCartRecipe[])
  ) => void;
};

const serviceId = (new URLSearchParams(window.location.search).get(
  "serviceId"
) || "billa") as "gurkerl" | "billa";

export const CartContext = createContext<TCartContext>({
  cart: {} as TCartContext["cart"],
  serviceId,
  recipeList: [] as TCartRecipe[],
  itemList: [],
  suggestedRecipes: [],
  expandedOptions: undefined,
  total: 0,
  step: "recipe",
  setStep: () => {},
  setSuggestedRecipes: () => {},
  checkout: () => {},
  setExpandedOptions: () => {},
  swapItem: () => {},
  swapRecipeItem: () => {},
  addToCart: () => {},
  removeFromCart: () => {},
  increaseQuantity: () => {},
  decreaseQuantity: () => {},
  addRecipe: () => {},
  increaseRecipeQuantity: () => {},
  decreaseRecipeQuantity: () => {},
  removeRecipe: () => {},
  increaseRecipeItemQuantity: () => {},
  decreaseRecipeItemQuantity: () => {},
  addRecipeItemsToCart: () => {},
  removeRecipeItemsFromCart: () => {},
  setRecipeList: () => {},
  searchQuery: "",
  setSearchQuery: () => {},
});

export const useCart = () => {
  return useContext(CartContext);
};

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [itemList, setItemList] = useState<TCartContext["itemList"]>([]);
  const [recipeList, setRecipeList] = useState<TCartContext["recipeList"]>([]);
  const [suggestedRecipes, setSuggestedRecipes] = useState<
    TCartContext["suggestedRecipes"]
  >([]);

  // Always set default step to "cart" instead of "recipe"
  const [step, setStep] = useState<TCartContext["step"]>("cart");

  const [expandedOptions, setExpandedOptions] = useState<string | undefined>(
    undefined
  );

  const [searchQuery, setSearchQuery] =
    useState<TCartContext["searchQuery"]>("");

  const value = useMemo(() => {
    return {
      serviceId,
      cart: itemList.reduce((acc, next) => {
        acc[next.product.id] = next;
        return acc;
      }, {} as TCartContext["cart"]),
      itemList,
      recipeList,
      suggestedRecipes,
      expandedOptions,
      total: rounding(
        [
          ...itemList,
          ...recipeList.flatMap((recipe) =>
            // Only include recipe items that aren't already in the main cart
            recipe.ingredients.filter(
              (ingredient) =>
                !itemList.some(
                  (item) => item.product.id === ingredient.product.id
                )
            )
          ),
        ].reduce((acc, item) => {
          if (
            !item.product ||
            !item.product.price ||
            typeof item.product.price.value !== "number"
          ) {
            return acc;
          }
          const price = Number(item.product.price.value);
          acc += price * (item.quantity || 1);
          return acc;
        }, 0) || 0
      ),
      step,
      searchQuery,
      setSearchQuery,

      setExpandedOptions,

      checkout() {
        if (serviceId === "gurkerl") {
          // First, add all products to Gurkerl cart via batch-add
          const allProducts = [
            ...itemList,
            ...recipeList.flatMap((recipe) => recipe.ingredients),
          ];

          console.log("Adding products to Gurkerl cart:", allProducts);

          if (allProducts.length === 0) {
            alert("No products in cart to add to Gurkerl");
            return;
          }

          const batchAddUrl =
            "https://www.gurkerl.at/batch-add?" +
            allProducts
              .reduce((acc, item) => {
                const index = acc.findIndex(([id]) => id === item.product.id);
                if (index === -1) {
                  acc.push([item.product.id, item.quantity]);
                } else {
                  acc[index][1] += item.quantity;
                }

                return acc;
              }, [] as [string, number][])

              .map(([id, quantity]) => `${id}=${quantity}`) // Format as URL params
              .join("&");

          console.log("Batch add URL:", batchAddUrl);

          // Open batch-add URL in a new tab
          const batchAddWindow = window.open(batchAddUrl);

          // After a short delay, redirect to the cart page
          setTimeout(() => {
            console.log("Redirecting to Gurkerl cart page...");
            if (batchAddWindow && !batchAddWindow.closed) {
              // If batch-add window is still open, redirect it to cart
              batchAddWindow.location.href =
                "https://www.gurkerl.at/bestellung/mein-warenkorb";
            } else {
              // If window was closed, open cart page in new tab
              window.open("https://www.gurkerl.at/bestellung/mein-warenkorb");
            }
          }, 2000); // Wait 2 seconds for batch-add to complete
        } else {
          alert("Checked out with " + serviceId);
        }
      },

      addToCart(products: TCartProduct[]) {
        setItemList((prev) => {
          // First, create a map of all products currently in the cart by ID
          const existingProductMap = new Map(
            prev.map((item) => [item.product.id, item])
          );

          // Process each product to add
          const updatedItems = [...prev];

          products.forEach(({ product, quantity, options = [] }) => {
            // Check if product already exists in cart
            if (existingProductMap.has(product.id)) {
              // Update existing item quantity
              const existingItem = existingProductMap.get(product.id)!;
              existingItem.quantity += quantity;
            } else {
              // Add as new item
              updatedItems.push({ quantity, product, options });
              existingProductMap.set(product.id, {
                quantity,
                product,
                options,
              });
            }
          });

          return updatedItems;
        });
      },

      removeFromCart(ids: string[]) {
        setItemList((prev) => {
          return prev.filter((item) => !ids.includes(item.product.id));
        });
      },

      increaseQuantity(id: string, quantity = 1) {
        setItemList((prev) => {
          return prev.map((item) =>
            item.product.id === id
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        });
      },

      decreaseQuantity(id: string, quantity = 1) {
        setItemList((prev) => {
          return prev
            .map((item) =>
              item.product.id === id
                ? { ...item, quantity: item.quantity - quantity }
                : item
            )
            .filter((item) => item.quantity > 0);
        });
      },

      swapItem(id: string, product: TProduct) {
        setExpandedOptions((prev) => (prev ? product.id : undefined));
        setItemList(([...prev]) => {
          const index = prev.findIndex((item) => item.product.id === id);

          const optionIndex = prev[index].options.findIndex(
            (option) => option.id === product.id
          );

          prev[index].options[optionIndex] = prev[index].product;

          const newItems = prev;

          newItems[index] = {
            product,
            quantity: 1,
            options: prev[index].options,
          };

          return newItems;
        });
      },
      setSuggestedRecipes(recipes: Recipe[]) {
        setSuggestedRecipes(recipes);
      },
      addRecipe(recipe: TCartRecipe) {
        setRecipeList((prev) => {
          // Check if recipe already exists in the list
          const recipeExists = prev.some(
            (existingRecipe) => existingRecipe.id === recipe.id
          );

          // If it exists, don't add it again
          if (recipeExists) {
            console.log(`Recipe with ID ${recipe.id} already exists in cart`);
            return prev;
          }

          // Add new recipe
          return [...prev, recipe];
        });
      },
      removeRecipe(id: string) {
        setRecipeList((prev) => {
          return prev.filter((recipe) => recipe.id !== id);
        });
      },
      increaseRecipeQuantity(recipeId: string) {
        setRecipeList((prev) => {
          return prev.map((recipe) =>
            recipe.id === recipeId
              ? {
                  ...recipe,
                  quantity: (recipe.quantity || 1) + 1,
                }
              : recipe
          );
        });
      },
      decreaseRecipeQuantity(recipeId: string) {
        setRecipeList((prev) => {
          return prev
            .map((recipe) =>
              recipe.id === recipeId
                ? {
                    ...recipe,
                    quantity: (recipe.quantity || 1) - 1,
                  }
                : recipe
            )
            .filter((recipe) => recipe.quantity > 0);
        });
      },
      increaseRecipeItemQuantity(
        recipeId: string,
        itemId: string,
        quantity = 1
      ) {
        setRecipeList((prev) => {
          return prev.map((recipe) => {
            if (recipe.id !== recipeId) return recipe;

            const index = recipe.ingredients.findIndex(
              (ingredient) => ingredient.product.id === itemId
            );

            if (index === -1) return recipe;

            recipe.ingredients[index].quantity += quantity;

            return {
              ...recipe,
              ingredients: recipe.ingredients.filter(
                (item) => item.quantity > 0
              ),
            };
          });
        });
      },
      decreaseRecipeItemQuantity(
        recipeId: string,
        itemId: string,
        quantity = 1
      ) {
        setRecipeList((prev) => {
          return prev.map((recipe) => {
            if (recipe.id !== recipeId) return recipe;

            const index = recipe.ingredients.findIndex(
              (ingredient) => ingredient.product.id === itemId
            );

            if (index === -1) return recipe;

            recipe.ingredients[index].quantity -= quantity;

            return {
              ...recipe,
              ingredients: recipe.ingredients.filter(
                (item) => item.quantity > 0
              ),
            };
          });
        });
      },
      swapRecipeItem(recipeId: string, itemId: string, product: TProduct) {
        setExpandedOptions((prev) => (prev ? product.id : undefined));
        setRecipeList((prev) => {
          return prev.map((recipe) => {
            const ingredients = recipe.ingredients;

            if (recipe.id !== recipeId) return recipe;

            const index = ingredients.findIndex(
              (ingredient) => ingredient.product.id === itemId
            );

            if (index === -1) return recipe;

            const optionIndex = ingredients[index].options.findIndex(
              (option) => option.id === product.id
            );

            if (optionIndex === -1) return recipe;

            ingredients[index].options[optionIndex] =
              ingredients[index].product;

            const newItems = ingredients;

            newItems[index] = {
              product,
              quantity: 1,
              options: ingredients[index].options,
            };

            return { ...recipe };
          });
        });
      },
      addRecipeItemsToCart(id: string, items: TCartProduct[]) {
        // Create a map of current cart items for efficient lookup
        const cartItemMap = new Map(
          itemList.map((item) => [item.product.id, item])
        );

        // Separate items into new ones and duplicates
        const newItems: TCartProduct[] = [];
        const duplicateItems: TCartProduct[] = [];

        items.forEach((item) => {
          if (cartItemMap.has(item.product.id)) {
            duplicateItems.push(item);
          } else {
            newItems.push(item);
            // Also add to map to prevent duplicates within the batch being added
            cartItemMap.set(item.product.id, item);
          }
        });

        // Add new items to cart
        if (newItems.length > 0) {
          this.addToCart(newItems);
        }

        // Increase quantity for existing items
        duplicateItems.forEach((item) => {
          this.increaseQuantity(item.product.id, item.quantity);
        });
      },
      removeRecipeItemsFromCart(recipeId: string) {
        const recipeItems = recipeList.find((recipe) => recipe.id === recipeId);
        if (!recipeItems) return;

        // Get all product IDs that are part of this recipe
        const productIdsToRemove = recipeItems.ingredients.map(
          (ingredient) => ingredient.product.id
        );

        // Remove these products from the cart
        setItemList((prev) => {
          return prev.filter(
            (item) => !productIdsToRemove.includes(item.product.id)
          );
        });

        // Now also remove the recipe from the recipe list
        // Just call removeRecipe without changing the view
        this.removeRecipe(recipeId);

        // Don't change the step/view here to prevent unintended navigation
      },
      setStep(step: "recipe" | "cart") {
        // Always force "cart" step regardless of input
        setStep("cart");
      },
      setRecipeList(
        recipes: TCartRecipe[] | ((prev: TCartRecipe[]) => TCartRecipe[])
      ) {
        setRecipeList(recipes);
      },
    };
  }, [
    itemList,
    setItemList,
    setExpandedOptions,
    expandedOptions,
    recipeList,
    setRecipeList,
    suggestedRecipes,
    step,
    setStep,
    // setSuggestedRecipes,
    searchQuery,
    setSearchQuery,
  ]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
